import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../api/axios';
import { ChevronRight, ChevronLeft, CheckCircle, AlertTriangle } from 'lucide-react';


const DIRECTIONS = [
  { val:'N', label:'North', short:'N' },
  { val:'S', label:'South', short:'S' },
  { val:'E', label:'East',  short:'E' },
  { val:'W', label:'West',  short:'W' },
];
const SHIFTS = [
  { val:'MORNING',   label:'Morning (6AM–2PM)' },
  { val:'AFTERNOON', label:'Afternoon (2PM–10PM)' },
  { val:'NIGHT',     label:'Night (10PM–6AM)' },
  { val:'FULL',      label:'Full Day' },
];
const PRODUCTION_STAGES = [
  { val:'BOX',     label:'BOX',          desc:'Raw block drilling/extraction' },
  { val:'PART',    label:'PART',         desc:'Part cutting operations' },
  { val:'GANGSAW', label:'GANGSAW',      desc:'Gangsaw slab operations' },
  { val:'GENERAL', label:'GENERAL WORK', desc:'General site operations' },
];
const WORK_TYPES = [
  { val:'DRILLING',   label:'Drilling' },
  { val:'WIRE_SAW',   label:'Wire Saw' },
  { val: 'BLADE_CUTTING', label:'Blade Cutting'},
  { val:'BLASTING',   label:'Blasting' },
  { val:'OFFSET',     label:'Offset Work' },
  { val:'WASTE',      label:'Waste Management' },
  { val:'TRANSPORT',  label:'Transport' },
  { val:'OTHER',      label:'Other Work' },
];
const OVERALL_STATUS_OPTIONS = [
  { val:'IN_PROGRESS', label:'In Progress',  desc:'Work is ongoing' },
  { val:'COMPLETED',   label:'Completed',    desc:'All work done — moves to next stage' },
  { val:'ON_HOLD',     label:'On Hold',      desc:'Work paused' },
];
const ENTRY_STATUS_OPTIONS = ['IN_PROGRESS','COMPLETED','ON_HOLD'];
const HOLD_REASONS    = ['Rain','Breakdown','Material Issue','Safety Issue','Labour Shortage','Other'];
const OPERATOR_ROLES  = ['Operator','Helper','Supervisor','Safety Officer','Driver','Assistant'];
const HOLE_OPTIONS    = ['H1','H2','H3','H4','H5','H6','H7','H8','H9','H10','H11','H12','H13','H14','H15','H16','H17','H18','H19','H20'];
const DRILL_CATEGORIES = [
  { val:'32MM',  label:'32mm Drilling Machine', askCutType:true },
  { val:'34MM',  label:'34mm Drilling Machine', askCutType:true },
  { val:'89MM',  label:'89mm Bore — 3.5 inch',  askCutType:false },
  { val:'102MM', label:'102mm Bore — 4 inch',   askCutType:false },
];
const MACHINES_BY_CATEGORY = {
  '32MM':  ['Jockey 1','Jockey 2','Jockey 3','Jockey 4','Jockey 5','Jockey 6','Jockey 7','Jockey 8','Jockey 9','Jockey 10','Sandvik DC120 Commando'],
  '34MM':  ['Jockey 1','Jockey 2','Jockey 3','Jockey 4','Jockey 5','Jockey 6','Jockey 7','Jockey 8','Jockey 9','Jockey 10','Sandvik DC120 Commando'],
  '89MM':  ['LD Bore','Sandvik Ranger DQ500'],
  '102MM': ['LD Bore','Sandvik Ranger DQ500'],
};
const CUT_TYPES   = ['Top Drilling','Side Cutting'];
const BORE_ANGLES = ['Vertical','Horizontal','Triangle','Cross','Others'];
const WIRESAW_CUTTING_TYPES = ['Edge Cutting','Side Cutting','Bottom Cutting','Vertical Cutting','Horizontal Cutting','Others'];
const WIRESAW_MACHINES      = ['75HP','60HP','20HP'];
const BLASTING_TYPES        = ['Cutting','Wasting'];
const BLASTING_SUBTYPES     = { Cutting:['5mg (Side Cutting)','10mg (Main Cutting)'], Wasting:['5mg','10mg'] };
const SAFETY_CHECKS         = ['Area Evacuated','Police Informed','Safety Zone Marked','Medical On Standby','Siren Active'];

const isRanger      = m => m === 'Sandvik Ranger DQ500';
const isDC120       = m => m === 'Sandvik DC120 Commando';
const isLDBore      = m => m === 'LD Bore';
const isBigBit      = m => isRanger(m) || isLDBore(m) || isDC120(m);
const isSmallBit    = m => !isBigBit(m);
const hasCompressor = m => !isRanger(m) && !isDC120(m);

const getDirShort   = dir => DIRECTIONS.find(d=>d.val===dir)?.short||dir;
const getBoxLabel   = (dir, block) => {
  if(!block) return '';
  if(!dir) return block.block_number||'';
  const serial = (block.block_number||'').split('/').pop()||'?';
  return `${getDirShort(dir)}-BOX/${serial}`;
};
const getPartLabel    = (dir,block,part)  => !part?'':!block?part.part_code||'':`${getBoxLabel(dir,block)}/${part.part_number||part.part_code||'?'}`;
const getGangsawLabel = (dir,block,part,slab) => !slab?'':`${getPartLabel(dir,block,part)}/${slab.slab_number||slab.slab_code||'?'}`;

const ST = ({ children }) => (
  <div style={{ fontSize:11, fontWeight:700, letterSpacing:'1.2px', textTransform:'uppercase', color:'var(--brand)', borderBottom:'2px solid var(--border)', paddingBottom:8, marginBottom:16 }}>{children}</div>
);
const Field = ({ label, required, full, children }) => (
  <div style={full?{gridColumn:'1/-1'}:{}} className="form-group">
    <label className="form-label">{label}{required&&<span style={{ color:'var(--red)',marginLeft:3 }}>*</span>}</label>
    {children}
  </div>
);
const CardBox = ({ children, amber, red }) => (
  <div style={{ background:red?'var(--red-bg)':amber?'var(--amber-bg)':'var(--bg-primary)', border:`1px solid ${red?'var(--red-border)':amber?'var(--amber-border)':'var(--border)'}`, borderRadius:'var(--radius-sm)', padding:14, marginBottom:12 }}>{children}</div>
);
const SL = ({ children, amber, red }) => (
  <div style={{ fontSize:11, fontWeight:700, color:red?'var(--red)':amber?'var(--amber)':'var(--brand)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:12 }}>{children}</div>
);
const AutoCalc = ({ label, value }) => (
  <Field label={label}><input className="form-input" readOnly value={value} style={{ background:'var(--blue-bg)', color:'var(--brand)', fontWeight:700, cursor:'not-allowed' }}/></Field>
);
const MachineBtn = ({ label, active, onClick }) => (
  <div onClick={onClick} style={{ padding:'10px 16px', borderRadius:'var(--radius-sm)', cursor:'pointer', textAlign:'center', border:`2px solid ${active?'var(--brand)':'var(--border)'}`, background:active?'var(--brand-dim)':'var(--white)', fontSize:13, fontWeight:700, color:active?'var(--brand)':'var(--text-secondary)' }}>
    {active?'✓ ':''}{label}
  </div>
);
const StepBar = ({ step }) => (
  <div style={{ display:'flex', alignItems:'center', marginBottom:28, flexWrap:'wrap', gap:4 }}>
    {['Session Info','Work Details','Review & Save'].map((label,i)=>{
      const num=i+1; const done=step>num; const active=step===num;
      return (
        <React.Fragment key={i}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <div style={{ width:30, height:30, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:12, flexShrink:0, background:done?'var(--green)':active?'var(--brand)':'var(--bg-primary)', color:done||active?'#fff':'var(--text-muted)', border:`2px solid ${done?'var(--green)':active?'var(--brand)':'var(--border)'}` }}>{done?'✓':num}</div>
            <span style={{ fontSize:12, fontWeight:active?600:400, color:active?'var(--text-primary)':'var(--text-muted)', whiteSpace:'nowrap' }}>{label}</span>
          </div>
          {i<2&&<div style={{ flex:1, height:1, background:'var(--border)', margin:'0 8px', minWidth:16 }}/>}
        </React.Fragment>
      );
    })}
  </div>
);

const HoleDetails = ({ holes, onChange }) => {
  const [sel,setSel]=useState(''); const [ft,setFt]=useState(''); const [dia,setDia]=useState('');
  const used=holes.map(h=>h.hole); const avail=HOLE_OPTIONS.filter(h=>!used.includes(h));
  const total=holes.reduce((s,h)=>s+(parseFloat(h.running_feet)||0),0).toFixed(2);
  const add=()=>{ if(!sel||!ft)return; onChange([...holes,{hole:sel,running_feet:ft,diameter:dia}]); setSel('');setFt('');setDia(''); };
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(120px, 1fr))', gap:10, marginBottom:12, alignItems:'end' }}>
        <Field label="Select Hole *"><select className="form-input" value={sel} onChange={e=>setSel(e.target.value)}><option value="">-- Select --</option>{avail.map(h=><option key={h}>{h}</option>)}</select></Field>
        <Field label="Running Feet *"><input className="form-input" type="number" step="0.01" min="0" value={ft} onChange={e=>setFt(e.target.value)} placeholder="0.00"/></Field>
        <Field label="Hole Diameter (mm)"><input className="form-input" type="number" step="0.1" value={dia} onChange={e=>setDia(e.target.value)} placeholder="e.g. 32.0"/></Field>
        <div className="form-group"><label className="form-label" style={{ opacity:0 }}>x</label><button type="button" className="btn btn-primary" onClick={add} disabled={!sel||!ft}>+ Add</button></div>
      </div>
      {holes.length>0&&(
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead><tr style={{ background:'var(--bg-primary)', borderBottom:'2px solid var(--border)' }}>
              {['Hole','Running Feet','Diameter','Remove'].map(h=><th key={h} style={{ padding:'6px 10px', textAlign:'left', fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase' }}>{h}</th>)}
            </tr></thead>
            <tbody>{holes.map((h,i)=>(
              <tr key={i} style={{ borderBottom:'1px solid var(--border-light)' }}>
                <td style={{ padding:'8px 10px', color:'var(--brand)', fontWeight:700 }}>{h.hole}</td>
                <td style={{ padding:'8px 10px', fontWeight:500 }}>{h.running_feet} ft</td>
                <td style={{ padding:'8px 10px', color:'var(--text-secondary)' }}>{h.diameter?`${h.diameter} mm`:'—'}</td>
                <td style={{ padding:'6px 10px' }}><button type="button" onClick={()=>onChange(holes.filter((_,j)=>j!==i))} style={{ background:'var(--red-bg)', border:'1px solid var(--red-border)', color:'var(--red)', width:28, height:28, borderRadius:'var(--radius-sm)', cursor:'pointer', fontSize:14 }}>×</button></td>
              </tr>
            ))}</tbody>
          </table>
          <div style={{ textAlign:'right', padding:'8px 10px', fontSize:13, fontWeight:700, color:'var(--brand)', borderTop:'2px solid var(--border)' }}>Total: {total} ft</div>
        </div>
      )}
    </div>
  );
};

const CompressorPanel = ({ data, onChange }) => {
  const set=(k,v)=>onChange({...data,[k]:v});
  const total=Math.max(0,(parseFloat(data.eb_end||0))-(parseFloat(data.eb_start||0))).toFixed(2);
  return (
    <CardBox>
      <SL>Compressor</SL>
      <div className="form-grid">
        <Field label="Points Used"><input className="form-input" type="number" min="0" value={data.points_used||''} onChange={e=>set('points_used',e.target.value)} placeholder="0"/></Field>
        <Field label="Pressure Level (PSI)"><input className="form-input" type="number" step="0.1" value={data.pressure_level||''} onChange={e=>set('pressure_level',e.target.value)} placeholder="0.0"/></Field>
        <Field label="Operation Type" full>
          <div style={{ display:'flex', gap:10 }}>
            {['Fuel','Current (EB)'].map(opt=>(
              <div key={opt} onClick={()=>set('operation_type',opt)} style={{ flex:1, padding:'9px 14px', borderRadius:'var(--radius-sm)', cursor:'pointer', border:`2px solid ${data.operation_type===opt?'var(--brand)':'var(--border)'}`, background:data.operation_type===opt?'var(--brand-dim)':'var(--white)', fontSize:13, fontWeight:600, textAlign:'center', color:data.operation_type===opt?'var(--brand)':'var(--text-secondary)' }}>
                {data.operation_type===opt?'✓ ':''}{opt}
              </div>
            ))}
          </div>
        </Field>
        {data.operation_type==='Fuel'&&<Field label="Fuel Consumption (L)"><input className="form-input" type="number" step="0.01" value={data.fuel||''} onChange={e=>set('fuel',e.target.value)} placeholder="0.00"/></Field>}
        {data.operation_type==='Current (EB)'&&<>
          <Field label="Starting Reading (Unit)"><input className="form-input" type="number" step="0.01" value={data.eb_start||''} onChange={e=>set('eb_start',e.target.value)} placeholder="0.00"/></Field>
          <Field label="Ending Reading (Unit)"><input className="form-input" type="number" step="0.01" value={data.eb_end||''} onChange={e=>set('eb_end',e.target.value)} placeholder="0.00"/></Field>
          <AutoCalc label="Total Units (Auto)" value={total}/>
        </>}
      </div>
    </CardBox>
  );
};

const OperatorPanel = ({ data, onChange, employees, uid }) => {
  const set=(k,v)=>onChange({...data,[k]:v});
  return (
    <CardBox>
      <SL>Operator Details</SL>
      <div className="form-grid">
        <Field label="Operator Name">
          <select className="form-input" value={data.operator_name||''} onChange={e=>set('operator_name',e.target.value)}>
            <option value="">Select operator</option>
            {employees.map(e=><option key={e.id} value={e.full_name}>{e.full_name} — {e.employee_id||e.designation||''}</option>)}
          </select>
        </Field>
        <Field label="Operator Role">
          <select className="form-input" value={data.operator_role||''} onChange={e=>set('operator_role',e.target.value)}>
            <option value="">Select role</option>{OPERATOR_ROLES.map(r=><option key={r}>{r}</option>)}
          </select>
        </Field>
      </div>
    </CardBox>
  );
};

const DrillMachinePanel = ({ machine, data, onChange, employees }) => {
  const set=(k,v)=>onChange({...data,[k]:v});
  const drillUsed=Math.max(0,(parseFloat(data.drill_bit_given||0))-(parseFloat(data.drill_bit_taken||0))).toFixed(3);
  const bigUsed=Math.max(0,(parseFloat(data.big_drill_bit_given||0))-(parseFloat(data.big_drill_bit_taken||0))).toFixed(3);
  const isR=isRanger(machine); const isDC=isDC120(machine);
  return (
    <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', overflow:'hidden', marginBottom:14 }}>
      <div style={{ background:'var(--brand)', padding:'10px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <span style={{ color:'#fff', fontWeight:700, fontSize:13 }}>{machine}</span>
        {isR&&<span style={{ fontSize:11, color:'rgba(255,255,255,0.8)', background:'rgba(255,255,255,0.15)', padding:'2px 10px', borderRadius:20 }}>GPRS Auto-fill</span>}
        {isDC&&<span style={{ fontSize:11, color:'rgba(255,255,255,0.8)', background:'rgba(255,255,255,0.15)', padding:'2px 10px', borderRadius:20 }}>DC120 Commando</span>}
      </div>
      <div style={{ padding:14, display:'flex', flexDirection:'column', gap:12 }}>
        {isR&&<div className="info-box" style={{ fontSize:12 }}>GPRS Auto-fill — Engine hours and fuel auto-filled from device.</div>}
        {isDC&&<div className="info-box" style={{ fontSize:12 }}>Sandvik DC120 Commando — Enter engine hours and fuel manually. No compressor required.</div>}
        <div className="form-grid">
          {(isR||isDC)?<>
            <Field label="Engine Hours Start"><input className="form-input" type="number" value={data.engine_hours_start||''} onChange={e=>set('engine_hours_start',e.target.value)} placeholder={isR?'Auto GPRS':'0'}/></Field>
            <Field label="Engine Hours End"><input className="form-input" type="number" value={data.engine_hours_end||''} onChange={e=>set('engine_hours_end',e.target.value)} placeholder={isR?'Auto GPRS':'0'}/></Field>
            <Field label="Fuel Consumed (L)"><input className="form-input" type="number" value={data.fuel_consumed||''} onChange={e=>set('fuel_consumed',e.target.value)} placeholder={isR?'Auto GPRS':'0'}/></Field>
          </>:<>
            <Field label="Running Hours"><input className="form-input" type="number" min="0" value={data.running_hours||''} onChange={e=>set('running_hours',e.target.value)} placeholder="0"/></Field>
            <Field label="Running Minutes"><input className="form-input" type="number" min="0" max="59" value={data.running_minutes||''} onChange={e=>set('running_minutes',e.target.value)} placeholder="0"/></Field>
          </>}
        </div>
        <CardBox>
          <SL>Hole Details <span style={{ color:'var(--text-muted)', fontWeight:400, textTransform:'none', fontSize:11 }}>(Running Feet per Hole)</span></SL>
          <HoleDetails holes={data.holes||[]} onChange={h=>set('holes',h)}/>
        </CardBox>
        {isSmallBit(machine)&&(
          <CardBox>
            <SL>Drill Bit Tracking</SL>
            <div className="form-grid">
              <Field label="Drill Bit Given (inches)"><input className="form-input" type="number" step="0.001" value={data.drill_bit_given||''} onChange={e=>set('drill_bit_given',e.target.value)} placeholder="0.000"/></Field>
              <Field label="Drill Bit Taken Back (inches)"><input className="form-input" type="number" step="0.001" value={data.drill_bit_taken||''} onChange={e=>set('drill_bit_taken',e.target.value)} placeholder="0.000"/></Field>
              <AutoCalc label="Drill Bit Used (Auto)" value={drillUsed}/>
            </div>
          </CardBox>
        )}
        {isBigBit(machine)&&(
          <CardBox amber>
            <SL amber>Big Drill Bit Tracking</SL>
            <div className="form-grid">
              <Field label="Big Drill Bit Given (inches)"><input className="form-input" type="number" step="0.001" value={data.big_drill_bit_given||''} onChange={e=>set('big_drill_bit_given',e.target.value)} placeholder="0.000"/></Field>
              <Field label="Big Drill Bit Taken Back (inches)"><input className="form-input" type="number" step="0.001" value={data.big_drill_bit_taken||''} onChange={e=>set('big_drill_bit_taken',e.target.value)} placeholder="0.000"/></Field>
              <AutoCalc label="Big Drill Bit Used (Auto)" value={bigUsed}/>
            </div>
          </CardBox>
        )}
        {hasCompressor(machine)&&<CompressorPanel data={data.compressor||{}} onChange={c=>set('compressor',c)}/>}
        <OperatorPanel data={data.operator||{}} onChange={o=>set('operator',o)} employees={employees} uid={`d-${machine}`}/>
      </div>
    </div>
  );
};

const WireSawMachinePanel = ({ machine, data, onChange, employees }) => {
  const set=(k,v)=>onChange({...data,[k]:v});
  const ebTotal=Math.max(0,(parseFloat(data.eb_end||0))-(parseFloat(data.eb_start||0))).toFixed(2);
  return (
    <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', overflow:'hidden', marginBottom:14 }}>
      <div style={{ background:'var(--brand)', padding:'10px 16px' }}><span style={{ color:'#fff', fontWeight:700, fontSize:13 }}>Wire Saw — {machine}</span></div>
      <div style={{ padding:14, display:'flex', flexDirection:'column', gap:12 }}>
        <div className="form-grid">
          <Field label="Cutting Area (Sq. Meter)" required><input className="form-input" type="number" step="0.01" value={data.cutting_area||''} onChange={e=>set('cutting_area',e.target.value)} placeholder="0.00"/></Field>
        </div>
        <CardBox>
          <SL>Rope Details</SL>
          <div className="form-grid">
            <Field label="Rope Consumption (Meter)"><input className="form-input" type="number" step="0.01" value={data.rope_consumption||''} onChange={e=>set('rope_consumption',e.target.value)} placeholder="0.00"/></Field>
            <Field label="Wastage Rope Weight (kg)"><input className="form-input" type="number" step="0.01" value={data.wastage_rope_weight||''} onChange={e=>set('wastage_rope_weight',e.target.value)} placeholder="0.00"/></Field>
            <Field label="Wastage Rope Sold?" full>
              <div style={{ display:'flex', gap:10 }}>
                {['Yes','No'].map(opt=><div key={opt} onClick={()=>set('rope_sold',opt)} style={{ flex:1, padding:'9px', borderRadius:'var(--radius-sm)', cursor:'pointer', border:`2px solid ${data.rope_sold===opt?'var(--brand)':'var(--border)'}`, background:data.rope_sold===opt?'var(--brand-dim)':'var(--white)', fontSize:13, fontWeight:600, textAlign:'center', color:data.rope_sold===opt?'var(--brand)':'var(--text-secondary)' }}>{data.rope_sold===opt?'✓ ':''}{opt}</div>)}
              </div>
            </Field>
            {data.rope_sold==='Yes'&&<>
              <Field label="Buyer Name"><input className="form-input" value={data.buyer_name||''} onChange={e=>set('buyer_name',e.target.value)}/></Field>
              <Field label="Buyer Details"><input className="form-input" value={data.buyer_details||''} onChange={e=>set('buyer_details',e.target.value)}/></Field>
              <Field label="Amount (₹)"><input className="form-input" type="number" value={data.rope_sold_amount||''} onChange={e=>set('rope_sold_amount',e.target.value)}/></Field>
            </>}
          </div>
        </CardBox>
        <CardBox>
          <SL>Operated By</SL>
          <div className="form-grid">
            <Field label="Power Source" full>
              <div style={{ display:'flex', gap:10 }}>
                {['Generator','Current (EB)'].map(opt=><div key={opt} onClick={()=>set('power_source',opt)} style={{ flex:1, padding:'9px', borderRadius:'var(--radius-sm)', cursor:'pointer', border:`2px solid ${data.power_source===opt?'var(--brand)':'var(--border)'}`, background:data.power_source===opt?'var(--brand-dim)':'var(--white)', fontSize:13, fontWeight:600, textAlign:'center', color:data.power_source===opt?'var(--brand)':'var(--text-secondary)' }}>{data.power_source===opt?'✓ ':''}{opt}</div>)}
              </div>
            </Field>
            {data.power_source==='Generator'&&<>
              <Field label="Running Hours"><input className="form-input" type="number" min="0" value={data.running_hours||''} onChange={e=>set('running_hours',e.target.value)} placeholder="0"/></Field>
              <Field label="Running Minutes"><input className="form-input" type="number" min="0" max="59" value={data.running_minutes||''} onChange={e=>set('running_minutes',e.target.value)} placeholder="0"/></Field>
              <Field label="Diesel Consumption (L)"><input className="form-input" type="number" step="0.01" value={data.diesel_consumption||''} onChange={e=>set('diesel_consumption',e.target.value)} placeholder="0.00"/></Field>
            </>}
            {data.power_source==='Current (EB)'&&<>
              <Field label="Starting Reading (Unit)"><input className="form-input" type="number" step="0.01" value={data.eb_start||''} onChange={e=>set('eb_start',e.target.value)} placeholder="0.00"/></Field>
              <Field label="Ending Reading (Unit)"><input className="form-input" type="number" step="0.01" value={data.eb_end||''} onChange={e=>set('eb_end',e.target.value)} placeholder="0.00"/></Field>
              <AutoCalc label="Total Units (Auto)" value={ebTotal}/>
            </>}
          </div>
        </CardBox>
        <OperatorPanel data={data.operator||{}} onChange={o=>set('operator',o)} employees={employees} uid={`ws-${machine}`}/>
      </div>
    </div>
  );
};

const DrillingSection = ({ data, onChange, employees }) => {
  const set=(k,v)=>onChange({...data,[k]:v});
  const cat=DRILL_CATEGORIES.find(c=>c.val===data.category);
  const machines=MACHINES_BY_CATEGORY[data.category]||[];
  const toggleM = m => {
    const pos = window.scrollY;
    const isSelected = (data.selected_machines||[]).includes(m);
    const md = isSelected ? {} : {[m]: (data.machine_data||{})[m]||{}};
    onChange({...data, selected_machines: isSelected?[]:[m], machine_data:md});
    setTimeout(() => window.scrollTo(0, pos), 50);
  };
  return (
    <div>
      <div className="card" style={{ marginBottom:14 }}>
        <ST>Drill Category *</ST>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:10 }}>
          {DRILL_CATEGORIES.map(c=><MachineBtn key={c.val} label={c.label} active={data.category===c.val} onClick={()=>onChange({...data,category:c.val,selected_machines:[],cut_type:'',bore_angle:'',machine_data:{}})}/>)}
        </div>
      </div>
      {data.category&&<>
        <div className="card" style={{ marginBottom:14 }}>
          <ST>{cat?.askCutType?'Drilling Type *':'Bore Angle *'} <span style={{ fontSize:10, fontWeight:400, color:'var(--text-muted)' }}>(For record only)</span></ST>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            {(cat?.askCutType?CUT_TYPES:BORE_ANGLES).map(t=>(
              <div key={t} onClick={()=>cat?.askCutType?set('cut_type',t):set('bore_angle',t)} style={{ padding:'9px 16px', borderRadius:'var(--radius-sm)', cursor:'pointer', border:`2px solid ${(data.cut_type||data.bore_angle)===t?'var(--brand)':'var(--border)'}`, background:(data.cut_type||data.bore_angle)===t?'var(--brand-dim)':'var(--white)', fontSize:12, fontWeight:600, color:(data.cut_type||data.bore_angle)===t?'var(--brand)':'var(--text-secondary)' }}>
                {(data.cut_type||data.bore_angle)===t?'✓ ':''}{t}
              </div>
            ))}
          </div>
        </div>
        <div className="card" style={{ marginBottom:14 }}>
          <ST>Select Machines *</ST>
          <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
            {machines.map(m=><MachineBtn key={m} label={m} active={(data.selected_machines||[]).includes(m)} onClick={()=>toggleM(m)}/>)}
          </div>
        </div>
        {(data.selected_machines||[]).map(machine=>(
          <DrillMachinePanel key={machine} machine={machine} data={(data.machine_data||{})[machine]||{}}
            onChange={d=>set('machine_data',{...data.machine_data,[machine]:d})} employees={employees}/>
        ))}
      </>}
    </div>
  );
};

const WireSawSection = ({ data, onChange, employees }) => {
  const set=(k,v)=>onChange({...data,[k]:v});
  const toggleM = m => {
    const pos = window.scrollY;
    const isSelected = (data.selected_machines||[]).includes(m);
    const md = isSelected ? {} : {[m]: (data.machine_data||{})[m]||{}};
    onChange({...data, selected_machines: isSelected?[]:[m], machine_data:md});
    setTimeout(() => window.scrollTo(0, pos), 50);
  };
  return (
    <div>
      <div className="card" style={{ marginBottom:14 }}>
        <ST>Cutting Type *</ST>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          {WIRESAW_CUTTING_TYPES.map(t=>(
            <div key={t} onClick={()=>set('cutting_type',t)} style={{ padding:'9px 14px', borderRadius:'var(--radius-sm)', cursor:'pointer', border:`2px solid ${data.cutting_type===t?'var(--brand)':'var(--border)'}`, background:data.cutting_type===t?'var(--brand-dim)':'var(--white)', fontSize:12, fontWeight:600, color:data.cutting_type===t?'var(--brand)':'var(--text-secondary)' }}>
              {data.cutting_type===t?'✓ ':''}{t}
            </div>
          ))}
        </div>
      </div>
      <div className="card" style={{ marginBottom:14 }}>
        <ST>Select Wire Saw Machine *</ST>
        <div style={{ display:'flex', gap:10 }}>
          {WIRESAW_MACHINES.map(m=><MachineBtn key={m} label={m} active={(data.selected_machines||[]).includes(m)} onClick={()=>toggleM(m)}/>)}
        </div>
      </div>
      {(data.selected_machines||[]).map(machine=>(
        <WireSawMachinePanel key={machine} machine={machine} data={(data.machine_data||{})[machine]||{}}
          onChange={d=>set('machine_data',{...data.machine_data,[machine]:d})} employees={employees}/>
      ))}
    </div>
  );
};

const BladeCuttingSection = ({ data, onChange, employees }) => {
  const set = (k, v) => onChange({...data, [k]:v});
  const BLADE_SIZES = ['140 cm', '220 cm'];
  const CUTTING_TYPES = ['Cross Cutting', 'Side Cutting'];
  const POWER_SOURCES = ['Generator', 'Current'];

  return (
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      {/* Blade Size */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))',gap:12}}>
        <div className="form-group">
          <label className="form-label">Blade Size (Cutter Size)</label>
          <select className="form-input" value={data.blade_size||''} onChange={e=>set('blade_size',e.target.value)}>
            <option value="">Select Blade Size</option>
            {BLADE_SIZES.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Cutting Type</label>
          <select className="form-input" value={data.cutting_type||''} onChange={e=>set('cutting_type',e.target.value)}>
            <option value="">Select Cutting Type</option>
            {CUTTING_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Number of Frames</label>
          <input className="form-input" type="number" value={data.number_of_frames||''} onChange={e=>set('number_of_frames',e.target.value)} placeholder="e.g. 5"/>
        </div>
        <div className="form-group">
          <label className="form-label">Running Hours</label>
          <input className="form-input" type="number" value={data.running_hours||''} onChange={e=>set('running_hours',e.target.value)} placeholder="e.g. 4"/>
        </div>
      </div>

      {/* Power Source */}
      <div className="form-group">
        <label className="form-label">Operated By</label>
        <div style={{display:'flex',gap:10}}>
          {POWER_SOURCES.map(s=>(
            <button key={s} type="button"
              className={`btn ${data.power_source===s?'btn-primary':'btn-secondary'}`}
              onClick={()=>set('power_source',s)}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Generator Fields */}
      {data.power_source==='Generator' && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(120px, 1fr))',gap:12,padding:12,background:'var(--bg-primary)',borderRadius:'var(--radius-sm)'}}>
          <div className="form-group">
            <label className="form-label">Running Hours</label>
            <input className="form-input" type="number" value={data.generator_hours||''} onChange={e=>set('generator_hours',e.target.value)} placeholder="e.g. 4"/>
          </div>
          <div className="form-group">
            <label className="form-label">Running Minutes</label>
            <input className="form-input" type="number" value={data.generator_minutes||''} onChange={e=>set('generator_minutes',e.target.value)} placeholder="e.g. 30"/>
          </div>
          <div className="form-group">
            <label className="form-label">Diesel Consumption (L)</label>
            <input className="form-input" type="number" step="0.01" value={data.diesel_consumption||''} onChange={e=>set('diesel_consumption',e.target.value)} placeholder="e.g. 10"/>
          </div>
        </div>
      )}

      {/* Current Fields */}
      {data.power_source==='Current' && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,padding:12,background:'var(--bg-primary)',borderRadius:'var(--radius-sm)'}}>
          <div className="form-group">
            <label className="form-label">Starting Reading (Units)</label>
            <input className="form-input" type="number" value={data.eb_start||''} onChange={e=>set('eb_start',e.target.value)} placeholder="e.g. 1000"/>
          </div>
          <div className="form-group">
            <label className="form-label">Ending Reading (Units)</label>
            <input className="form-input" type="number" value={data.eb_end||''} onChange={e=>set('eb_end',e.target.value)} placeholder="e.g. 1050"/>
          </div>
          <div className="form-group">
            <label className="form-label">Total Units</label>
            <input className="form-input" readOnly
              value={data.eb_start&&data.eb_end ? (parseFloat(data.eb_end)-parseFloat(data.eb_start)).toFixed(2) : ''}
              style={{background:'var(--brand-dim)',fontWeight:700,color:'var(--brand)'}}/>
          </div>
        </div>
      )}

      {/* Operator */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))',gap:12,padding:12,background:'var(--bg-primary)',borderRadius:'var(--radius-sm)'}}>
        <div className="form-group">
          <label className="form-label">Operator Name</label>
          <select className="form-input" value={data.operator?.operator_name||''} onChange={e=>{
            const emp = employees.find(x=>x.full_name===e.target.value);
            set('operator',{operator_name:e.target.value, operator_role:emp?.designation||'Operator'});
          }}>
            <option value="">Select Operator</option>
            {(employees||[]).map(e=><option key={e.id} value={e.full_name}>{e.full_name}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Role</label>
          <input className="form-input" value={data.operator?.operator_role||''} onChange={e=>set('operator',{...data.operator,operator_role:e.target.value})} placeholder="e.g. Operator"/>
        </div>
      </div>
    </div>
  );
};



const BlastingSection = ({ data, onChange, employees }) => {
  const set=(k,v)=>onChange({...data,[k]:v});
  return (
    <div>
      <div className="card" style={{ marginBottom:14 }}>
        <ST>Blasting Type *</ST>
        <div style={{ display:'flex', gap:12 }}>
          {BLASTING_TYPES.map(t=>(
            <div key={t} onClick={()=>onChange({...data,blasting_type:t,sub_type:''})} style={{ flex:1, padding:'14px', borderRadius:'var(--radius-sm)', cursor:'pointer', textAlign:'center', border:`2px solid ${data.blasting_type===t?'var(--brand)':'var(--border)'}`, background:data.blasting_type===t?'var(--brand-dim)':'var(--white)', fontSize:14, fontWeight:700, color:data.blasting_type===t?'var(--brand)':'var(--text-secondary)' }}>
              {data.blasting_type===t?'✓ ':''}{t}
            </div>
          ))}
        </div>
      </div>
      {data.blasting_type&&<>
        <div className="card" style={{ marginBottom:14 }}>
          <ST>Sub Type *</ST>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            {(BLASTING_SUBTYPES[data.blasting_type]||[]).map(t=>(
              <div key={t} onClick={()=>set('sub_type',t)} style={{ padding:'9px 14px', borderRadius:'var(--radius-sm)', cursor:'pointer', border:`2px solid ${data.sub_type===t?'var(--brand)':'var(--border)'}`, background:data.sub_type===t?'var(--brand-dim)':'var(--white)', fontSize:12, fontWeight:600, color:data.sub_type===t?'var(--brand)':'var(--text-secondary)' }}>
                {data.sub_type===t?'✓ ':''}{t}
              </div>
            ))}
          </div>
        </div>
        <div className="card" style={{ marginBottom:14 }}>
          <ST>Blasting Details</ST>
          <div className="form-grid">
            <Field label="Number of Detonator (Cape) Used"><input className="form-input" type="number" min="0" value={data.detonator_count||''} onChange={e=>set('detonator_count',e.target.value)} placeholder="0"/></Field>
            <Field label="Thiri / Fuse Wire Used (feet)"><input className="form-input" type="number" step="0.01" value={data.thiri_used||''} onChange={e=>set('thiri_used',e.target.value)} placeholder="0.00"/></Field>
            <Field label="Hole Feet (depth)"><input className="form-input" type="number" step="0.01" value={data.hole_feet||''} onChange={e=>set('hole_feet',e.target.value)} placeholder="0.00"/></Field>
          </div>
          <CardBox red>
            <SL red>Safety Checklist</SL>
            <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
              {SAFETY_CHECKS.map(c=>(
                <label key={c} style={{ display:'flex', alignItems:'center', gap:7, fontSize:13, cursor:'pointer' }}>
                  <input type="checkbox" checked={data.safety?.[c]||false} onChange={e=>set('safety',{...data.safety,[c]:e.target.checked})} style={{ width:14, height:14 }}/>{c}
                </label>
              ))}
            </div>
          </CardBox>
        </div>
        <div className="card" style={{ marginBottom:14 }}>
          <OperatorPanel data={data.operator||{}} onChange={o=>set('operator',o)} employees={employees} uid="blast"/>
        </div>
      </>}
    </div>
  );
};



const OffsetSection = ({ data, onChange, employees }) => {
  const set=(k,v)=>onChange({...data,[k]:v});
  return (
    <div className="card" style={{ marginBottom:14 }}>
      <ST>Offset Work Details</ST>
      <div className="form-grid">
        <Field label="Offset Distance (m)"><input className="form-input" type="number" step="0.01" value={data.offset_distance||''} onChange={e=>set('offset_distance',e.target.value)} placeholder="0.00"/></Field>
        <Field label="Offset Direction"><select className="form-input" value={data.offset_direction||''} onChange={e=>set('offset_direction',e.target.value)}><option value="">Select</option>{['Left','Right','Forward','Backward'].map(d=><option key={d}>{d}</option>)}</select></Field>
        <Field label="Correction Type"><select className="form-input" value={data.correction_type||''} onChange={e=>set('correction_type',e.target.value)}><option value="">Select</option>{['Level','Angle','Position'].map(t=><option key={t}>{t}</option>)}</select></Field>
        <Field label="Machine Used"><select className="form-input" value={data.machine_used||''} onChange={e=>set('machine_used',e.target.value)}><option value="">Select</option>{['Poclain','Tipper','Manual'].map(m=><option key={m}>{m}</option>)}</select></Field>
        <Field label="Correction Details" full><textarea className="form-input" rows={2} value={data.correction_details||''} onChange={e=>set('correction_details',e.target.value)}/></Field>
      </div>
      <div style={{ marginTop:14 }}><OperatorPanel data={data.operator||{}} onChange={o=>set('operator',o)} employees={employees} uid="offset"/></div>
    </div>
  );
};

const WasteSection = ({ data, onChange, employees }) => {
  const set=(k,v)=>onChange({...data,[k]:v});
  return (
    <div className="card" style={{ marginBottom:14 }}>
      <ST>Waste Management Details</ST>
      <div className="form-grid">
        <Field label="Waste Source"><select className="form-input" value={data.waste_source||''} onChange={e=>set('waste_source',e.target.value)}><option value="">Select</option>{['Block','Part','Gangsaw Slab'].map(s=><option key={s}>{s}</option>)}</select></Field>
        <Field label="Reference ID"><input className="form-input" value={data.reference_id||''} onChange={e=>set('reference_id',e.target.value)}/></Field>
        <Field label="Reason"><select className="form-input" value={data.reason||''} onChange={e=>set('reason',e.target.value)}><option value="">Select reason</option>{['Crack','Band','Color Variation','Size Issue','Natural Break','Other'].map(r=><option key={r}>{r}</option>)}</select></Field>
        <Field label="Volume Loss (m³)"><input className="form-input" type="number" step="0.01" value={data.volume_loss||''} onChange={e=>set('volume_loss',e.target.value)}/></Field>
        <Field label="Disposed?" full>
          <div style={{ display:'flex', gap:10 }}>
            {['Yes','No'].map(opt=><div key={opt} onClick={()=>set('disposed',opt)} style={{ flex:1, padding:'9px', borderRadius:'var(--radius-sm)', cursor:'pointer', border:`2px solid ${data.disposed===opt?'var(--brand)':'var(--border)'}`, background:data.disposed===opt?'var(--brand-dim)':'var(--white)', fontSize:13, fontWeight:600, textAlign:'center', color:data.disposed===opt?'var(--brand)':'var(--text-secondary)' }}>{data.disposed===opt?'✓ ':''}{opt}</div>)}
          </div>
        </Field>
        {data.disposed==='Yes'&&<>
          <Field label="Disposed To"><select className="form-input" value={data.disposed_to||''} onChange={e=>set('disposed_to',e.target.value)}><option value="">Select</option>{['Crusher','Dumped','Sold'].map(d=><option key={d}>{d}</option>)}</select></Field>
          {data.disposed_to==='Sold'&&<>
            <Field label="Buyer Name"><input className="form-input" value={data.buyer_name||''} onChange={e=>set('buyer_name',e.target.value)}/></Field>
            <Field label="Amount (₹)"><input className="form-input" type="number" value={data.sold_amount||''} onChange={e=>set('sold_amount',e.target.value)}/></Field>
          </>}
        </>}
      </div>
      <div style={{ marginTop:14 }}><OperatorPanel data={data.operator||{}} onChange={o=>set('operator',o)} employees={employees} uid="waste"/></div>
    </div>
  );
};

const OtherSection = ({ data, onChange, employees }) => {
  const set=(k,v)=>onChange({...data,[k]:v});
  return (
    <div className="card" style={{ marginBottom:14 }}>
      <ST>Other Work Details</ST>
      <div className="form-grid">
        <Field label="Work Description" full><textarea className="form-input" rows={2} value={data.description||''} onChange={e=>set('description',e.target.value)} placeholder="Describe what work was done"/></Field>
        <Field label="Machine Used"><input className="form-input" value={data.machine_used||''} onChange={e=>set('machine_used',e.target.value)}/></Field>
        <Field label="Hours Spent"><input className="form-input" type="number" step="0.1" value={data.hours_spent||''} onChange={e=>set('hours_spent',e.target.value)}/></Field>
        <Field label="Materials Used" full><input className="form-input" value={data.materials_used||''} onChange={e=>set('materials_used',e.target.value)}/></Field>
      </div>
      <div style={{ marginTop:14 }}><OperatorPanel data={data.operator||{}} onChange={o=>set('operator',o)} employees={employees} uid="other"/></div>
    </div>
  );
};

const TransportSection = ({ data, onChange, employees }) => {
  const set=(k,v)=>onChange({...data,[k]:v});
  return (
    <div className="card" style={{ marginBottom:14 }}>
      <ST>Transport Details</ST>
      <div className="form-grid">
        <Field label="Vehicle Type" required>
          <select className="form-input" value={data.vehicle_type||''} onChange={e=>set('vehicle_type',e.target.value)}>
            <option value="">Select vehicle</option>
            {['Tipper','Poclain','JCB','Tractor','Manual'].map(v=><option key={v}>{v}</option>)}
          </select>
        </Field>
        <Field label="Number of Trips"><input className="form-input" type="number" min="0" value={data.trips||''} onChange={e=>set('trips',e.target.value)} placeholder="0"/></Field>
        <Field label="Load per Trip (Tonnes)"><input className="form-input" type="number" step="0.1" value={data.load_per_trip||''} onChange={e=>set('load_per_trip',e.target.value)} placeholder="0.0"/></Field>
        <Field label="Total Load (Auto)"><input className="form-input" readOnly value={((parseFloat(data.trips||0))*(parseFloat(data.load_per_trip||0))).toFixed(2)+' T'} style={{ background:'var(--blue-bg)', color:'var(--brand)', fontWeight:700, cursor:'not-allowed' }}/></Field>
        <Field label="Material Type">
          <select className="form-input" value={data.material_type||''} onChange={e=>set('material_type',e.target.value)}>
            <option value="">Select material</option>
            {['Rock','Soil','Waste','Block','Part','Other'].map(m=><option key={m}>{m}</option>)}
          </select>
        </Field>
        <Field label="From Location"><input className="form-input" value={data.from_location||''} onChange={e=>set('from_location',e.target.value)} placeholder="e.g. North Face"/></Field>
        <Field label="To Location"><input className="form-input" value={data.to_location||''} onChange={e=>set('to_location',e.target.value)} placeholder="e.g. Yard / Crusher"/></Field>
        <Field label="Fuel Consumption (L)"><input className="form-input" type="number" step="0.01" value={data.fuel_consumption||''} onChange={e=>set('fuel_consumption',e.target.value)} placeholder="0.00"/></Field>
        <Field label="Driver Name" full>
          <select className="form-input" value={data.driver_name||''} onChange={e=>set('driver_name',e.target.value)}>
            <option value="">Select driver</option>
            {(employees||[]).map(e=><option key={e.id} value={e.full_name}>{e.full_name}</option>)}
          </select>
        </Field>
      </div>
    </div>
  );
};

const WorkTypeSection = ({ workType, data, onChange, employees }) => {
  if(workType==='DRILLING')  return <DrillingSection  data={data} onChange={onChange} employees={employees}/>;
  if(workType==='WIRE_SAW')  return <WireSawSection   data={data} onChange={onChange} employees={employees}/>;
  if(workType==='BLADE_CUTTING') return <BladeCuttingSection data={data} onChange={onChange} employees={employees}/>;
  if(workType==='BLASTING')  return <BlastingSection  data={data} onChange={onChange} employees={employees}/>;
  if(workType==='OFFSET')    return <OffsetSection    data={data} onChange={onChange} employees={employees}/>;
  if(workType==='WASTE')     return <WasteSection      data={data} onChange={onChange} employees={employees}/>;
  if(workType==='TRANSPORT') return <TransportSection  data={data} onChange={onChange} employees={employees}/>;
  if(workType==='OTHER')     return <OtherSection      data={data} onChange={onChange} employees={employees}/>;
  return null;
};

export const DayWorkEntries = ({ entries, onChange, employees }) => {
  const addEntry = () => onChange([...entries, { work_type:'', data:{}, status:'IN_PROGRESS', hold_reason:'' }]);
  const updateEntry = (i, entry) => { const e=[...entries]; e[i]=entry; onChange(e); };
  const removeEntry = i => onChange(entries.filter((_,j)=>j!==i));
  return (
    <div>
      {entries.map((entry, i) => (
        <div key={i} style={{ border:`2px solid ${entry.status==='COMPLETED'?'var(--green)':entry.status==='ON_HOLD'?'var(--amber)':'var(--border)'}`, borderRadius:'var(--radius)', marginBottom:16, overflow:'hidden' }}>
          <div style={{ background:'var(--bg-hover)', padding:'10px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', borderBottom:'1px solid var(--border)', flexWrap:'wrap', gap:8 }}>
            <div style={{ fontSize:13, fontWeight:700, color:'var(--brand)' }}>
              Work Entry {i+1}
              {entry.work_type && <span style={{ fontSize:12, color:'var(--text-muted)', fontWeight:400, marginLeft:8 }}>— {WORK_TYPES.find(w=>w.val===entry.work_type)?.label||entry.work_type}</span>}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
              <div style={{ display:'flex', gap:6 }}>
                {ENTRY_STATUS_OPTIONS.map(s=>(
                  <div key={s} onClick={()=>updateEntry(i,{...entry,status:s})} style={{ padding:'5px 12px', borderRadius:'var(--radius-sm)', cursor:'pointer', fontSize:11, fontWeight:700, border:`1px solid ${entry.status===s?'var(--brand)':'var(--border)'}`, background:entry.status===s?'var(--brand-dim)':'var(--white)', color:entry.status===s?'var(--brand)':'var(--text-muted)' }}>
                    {s==='IN_PROGRESS'?'In Progress':s==='COMPLETED'?'✓ Completed':'⏸ On Hold'}
                  </div>
                ))}
              </div>
              {entry.status==='ON_HOLD'&&(
                <select className="form-input" style={{ width:'auto', fontSize:12, padding:'4px 8px' }} value={entry.hold_reason||''} onChange={e=>updateEntry(i,{...entry,hold_reason:e.target.value})}>
                  <option value="">Hold reason</option>
                  {HOLD_REASONS.map(r=><option key={r}>{r}</option>)}
                </select>
              )}
              {entries.length>1&&<button type="button" className="btn btn-sm btn-danger" onClick={()=>removeEntry(i)}>Remove</button>}
            </div>
          </div>
          <div style={{ padding:16 }}>
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--brand)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:10 }}>Work Type *</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:8 }}>
                {WORK_TYPES.map(wt=>(
                  <div key={wt.val} onClick={()=>updateEntry(i,{...entry,work_type:wt.val,data:{}})} style={{ padding:'10px', borderRadius:'var(--radius-sm)', cursor:'pointer', textAlign:'center', border:`2px solid ${entry.work_type===wt.val?'var(--brand)':'var(--border)'}`, background:entry.work_type===wt.val?'var(--brand-dim)':'var(--white)', fontSize:13, fontWeight:700, color:entry.work_type===wt.val?'var(--brand)':'var(--text-secondary)' }}>
                    {entry.work_type===wt.val?'✓ ':''}{wt.label}
                  </div>
                ))}
              </div>
            </div>
            {entry.work_type&&(
              <WorkTypeSection workType={entry.work_type} data={entry.data}
                onChange={d=>updateEntry(i,{...entry,data:d})} employees={employees}/>
            )}
            {/* Remarks & Photo */}
            <div style={{ marginTop:14, borderTop:'1px solid var(--border-light)', paddingTop:14 }}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:12 }}>
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">Remarks / Notes</label>
                <textarea className="form-input" rows={2} 
                value={entry.remarks||''} 
                onChange={e=>updateEntry(i,{...entry,remarks:e.target.value})}
                placeholder="Any observations, issues or notes for this work entry..."/>
              </div>
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
              <label className="form-label">Photo (Optional)</label>
              <input type="file" accept="image/*" className="form-input"
              onChange={e=>{
                const file = e.target.files[0];
                if(file) updateEntry(i,{...entry,photo:file});
              }}
              style={{ padding:'6px 12px' }}/>
              {entry.photo && (
                <div style={{ fontSize:12, color:'var(--green)', marginTop:4 }}>
                  ✓ {entry.photo.name}
                  </div>
                )}
                </div>
                </div>
              </div>
          </div>
        </div>
      ))}
      <button type="button" className="btn btn-secondary" onClick={addEntry} style={{ width:'100%', justifyContent:'center', borderStyle:'dashed', fontSize:13 }}>
        + Add Another Work for Today
      </button>
    </div>
  );
};

const EMPTY_FORM = {
  session_date: new Date().toISOString().split('T')[0],
  shift:'MORNING', location:'', direction:'', supervisor_name:'',
  production_stage:'', selected_block_id:'', selected_part_id:'', selected_gangsaw_id:'',
  overall_status:'IN_PROGRESS', hold_reason:'', notes:'',
  day_entries:[{ work_type:'', data:{}, status:'IN_PROGRESS', hold_reason:'' }],
  existing_entries:[],
  lbh_length: '', lbh_breadth: '', lbh_height: '',
};

export default function WorkSessionForm({ mode='new', sessionId=null, isResume=false }) {
  const navigate = useNavigate();
  const [step, setStep]         = useState(1);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [allBlocks, setAllBlocks] = useState([]);  // ALL blocks from DB
  const [dirBlocks, setDirBlocks] = useState([]);  // blocks filtered by direction
  const [parts, setParts]       = useState([]);
  const [gangsaws, setGangsaws] = useState([]);
  const [employees, setEmps]    = useState([]);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [warning, setWarning]   = useState('');
  const [origSession, setOrigSession] = useState(null);
  const attachRef = useRef();
  const isLoadingDir = useRef(false);

  const setF = (k,v) => setForm(f => ({...f,[k]:v}));

  // Load master data
  useEffect(()=>{
    api.get('/employees/?page_size=100&is_active=true').then(r=>setEmps(r.data.results||[]));
    api.get('/production/blocks/?page_size=100').then(r=>{
      const all = r.data.results||[];
      setAllBlocks(all);
    });
  },[]);

  // Filter blocks by direction using work sessions
  const filterBlocksByDirection = async (dir, allBlocksList, stage='BOX') => {
    if(!dir) { setDirBlocks([]); return { usedBlockIds:[], dirSessions:[], incomplete:[] }; }
    try {
      const res = await api.get(`/production/blocks/?direction=${dir}&page_size=200`);
      const dirBlocksFromApi = res.data.results||[];

      // Sort: oldest incomplete first (lowest block number = first one started)
      const incomplete = dirBlocksFromApi.filter(b=>b.status!=='COMPLETED')
        .sort((a,b)=>a.block_number.localeCompare(b.block_number));
      const completed  = dirBlocksFromApi.filter(b=>b.status==='COMPLETED')
        .sort((a,b)=>a.block_number.localeCompare(b.block_number));
      setDirBlocks([...incomplete,...completed]);

      const usedBlockIds = dirBlocksFromApi.map(b=>String(b.id));

      if(stage==='BOX') {
        // Auto-select current incomplete block
        if(incomplete.length>0) setF('selected_block_id', incomplete[0].id);
      }

      if(stage==='PART') {
        // Find completed block that has incomplete parts
        const completedBlocks = dirBlocksFromApi.filter(b=>b.status==='COMPLETED')
          .sort((a,b)=>a.block_number.localeCompare(b.block_number));
        for(const block of completedBlocks) {
          try {
            const partsRes = await api.get(`/production/parts/?block=${block.id}&page_size=100`);
            const allParts = partsRes.data.results||[];
            const incompletePart = allParts.find(p=>p.status==='AVAILABLE'||p.status==='IN_PROCESS');
            if(incompletePart) {
              setF('selected_block_id', block.id);
              setParts(allParts);
              setF('selected_part_id', incompletePart.id);
              break;
            }
          } catch {}
        }
      }

      if(stage==='GANGSAW') {
        // Find completed block that has part IN_GANGSAW
        const completedBlocks = dirBlocksFromApi.filter(b=>b.status==='COMPLETED')
          .sort((a,b)=>a.block_number.localeCompare(b.block_number));
        for(const block of completedBlocks) {
          try {
            const partsRes = await api.get(`/production/parts/?block=${block.id}&page_size=100`);
            const allParts = partsRes.data.results||[];
            const gangawPart = allParts.find(p=>p.status==='IN_GANGSAW');
            if(gangawPart) {
              setF('selected_block_id', block.id);
              setParts(allParts);
              setF('selected_part_id', gangawPart.id);
              // Load gangsaws
              const slabsRes = await api.get(`/production/slabs/?part=${gangawPart.id}&page_size=100`);
              const allSlabs = slabsRes.data.results||[];
              setGangsaws(allSlabs);
              const incompleteGangsaw = allSlabs.find(g=>g.status==='IN_PROCESS'||g.status==='QC_PENDING');
              if(incompleteGangsaw) setF('selected_gangsaw_id', incompleteGangsaw.id);
              break;
            }
          } catch {}
        }
      }

      return { usedBlockIds, dirSessions:[], incomplete };
    } catch {
      setDirBlocks([]);
      return { usedBlockIds:[], dirSessions:[], incomplete:[] };
    }
  };

  // Load parts when block changes
  useEffect(()=>{
    if(form.selected_block_id){
      api.get(`/production/parts/?block=${form.selected_block_id}&page_size=100`)
        .then(r=>{
          const res=r.data.results||[];
          setParts(res);
          const selBlock=allBlocks.find(b=>b.id===form.selected_block_id);
          if(res.length>0 && selBlock?.status==='COMPLETED'){
            setF('production_stage','PART');
            const incompletePart=res.find(p=>p.status==='AVAILABLE'||p.status==='IN_PROCESS');
            if(incompletePart && !form.selected_part_id) setF('selected_part_id',incompletePart.id);
          }
        }).catch(()=>setParts([]));
    } else { setParts([]); }
  },[form.selected_block_id, allBlocks]);

  // Load gangsaws when part changes
  useEffect(()=>{
    if(form.selected_part_id){
      api.get(`/production/slabs/?part=${form.selected_part_id}&page_size=100`)
        .then(r=>{
          const res=r.data.results||[];
          setGangsaws(res);
          const selPart=parts.find(p=>p.id===form.selected_part_id);
          if(res.length>0 && selPart?.status==='IN_GANGSAW'){
            setF('production_stage','GANGSAW');
            const incompleteSlab=res.find(g=>g.status==='IN_PROCESS'||g.status==='QC_PENDING');
            if(incompleteSlab && !form.selected_gangsaw_id) setF('selected_gangsaw_id',incompleteSlab.id);
          }
        }).catch(()=>setGangsaws([]));
    } else { setGangsaws([]); }
  },[form.selected_part_id, parts]);

  // Load session data for edit/resume
  useEffect(()=>{
    if((mode==='edit'||isResume) && sessionId){
      api.get(`/operations/sessions/${sessionId}/`).then(async r=>{
        const s = r.data?.data || r.data;
        setOrigSession(s);
        let existingEntries = [];
        let parsedNotes = '';
        let parsedHoldReason = '';
        let parsedOverallStatus = s.status||'IN_PROGRESS';
        const rawNotes = r.data?.notes || r.data?.data?.notes || s.notes || '';
        try {
          if(rawNotes && rawNotes.trim().startsWith('{')){
            const p = JSON.parse(rawNotes);
            if(Array.isArray(p.day_entries) && p.day_entries.length > 0) existingEntries = p.day_entries;
            parsedNotes = p.user_notes||'';
            parsedHoldReason = p.hold_reason||'';
          }
        } catch { existingEntries = []; }

        const today = new Date().toISOString().split('T')[0];
        const todayEntries = isResume
          ? [{ work_type:'', data:{}, status:'IN_PROGRESS', hold_reason:'', entry_date: today }]
          : existingEntries;

        setForm({
          ...EMPTY_FORM,
          session_date: mode==='edit' ? s.started_date : today,
          shift: s.shift||'MORNING',
          location: s.location||'',
          direction: s.direction||'',
          supervisor_name: s.supervisor_name||'',
          production_stage: s.production_stage||'',
          selected_block_id: String(s.block_id||''),
          selected_part_id: String(s.part_id||''),
          selected_gangsaw_id: String(s.gangsaw_id||''),
          overall_status: isResume ? 'IN_PROGRESS' : parsedOverallStatus,
          hold_reason: parsedHoldReason,
          notes: parsedNotes,
          lbh_length:  existingEntries.lbh?.length  || '',
          lbh_breadth: existingEntries.lbh?.breadth || '',
          lbh_height:  existingEntries.lbh?.height  || '',
          day_entries: todayEntries,
          existing_entries: existingEntries,
        });

        // Load direction-filtered blocks for edit/resume
        if(s.direction) {
          const blocksRes = await api.get('/production/blocks/?page_size=100');
          const allB = blocksRes.data.results||[];
          setAllBlocks(allB);
          await filterBlocksByDirection(s.direction, allB);
        }
        if(s.block_id) api.get(`/production/parts/?block=${s.block_id}&page_size=100`).then(r2=>setParts(r2.data.results||[]));
        if(s.part_id)  api.get(`/production/slabs/?part=${s.part_id}&page_size=100`).then(r2=>setGangsaws(r2.data.results||[]));
      }).catch(()=>navigate('/operations'));
    }
  },[sessionId, mode, isResume]);

  const selectedBlock   = allBlocks.find(b=>b.id===form.selected_block_id);
  const selectedPart    = parts.find(p=>p.id===form.selected_part_id);
  const selectedGangsaw = gangsaws.find(g=>g.id===form.selected_gangsaw_id);

  const refLabel = form.production_stage==='GANGSAW'&&form.selected_gangsaw_id ? getGangsawLabel(form.direction,selectedBlock,selectedPart,selectedGangsaw)
                 : form.production_stage==='PART'&&form.selected_part_id       ? getPartLabel(form.direction,selectedBlock,selectedPart)
                 : getBoxLabel(form.direction,selectedBlock)||selectedBlock?.block_number||'';

  const validateStep1 = () => {
    if(!form.shift)            return 'Please select Shift.';
    if(!form.direction)        return 'Please select Direction.';
    if(!form.supervisor_name)  return 'Please enter Supervisor Name.';
    if(!form.production_stage) return 'Please select Production Stage.';
    if(form.production_stage!=='GENERAL' && !form.selected_block_id) return 'Please select Block Reference.';
    if((form.production_stage==='PART'||form.production_stage==='GANGSAW') && !form.selected_part_id) return 'Please select Part Reference.';
    if(form.production_stage==='GANGSAW' && !form.selected_gangsaw_id) return 'Please select Gangsaw Reference.';
    if(form.overall_status==='ON_HOLD' && !form.hold_reason) return 'Please select Hold Reason.';
    return null;
  };

  const validateStep2 = () => {
    if(form.overall_status==='COMPLETED') {
      const inProgressEntries = form.day_entries.filter(e=>e.work_type && e.status==='IN_PROGRESS');
      if(inProgressEntries.length > 0) {
        const types = inProgressEntries.map(e=>e.work_type).join(', ');
        return `Overall Status is COMPLETED but these work entries are still IN_PROGRESS: ${types}. Please mark them as Completed or On Hold first.`;
      }
    }
    return null;
  };

  const handleSave = async () => {
    if(form.day_entries.every(e=>!e.work_type)){ setError('Please add at least one work entry.'); return; }
    setSaving(true); setError('');
    try {
      const today = new Date().toISOString().split('T')[0];
      const todayEntriesWithDate = form.day_entries.filter(e=>e.work_type).map(e=>({...e,entry_date:form.session_date||today}));
      let latestExistingEntries = [];
      let latestLbh = null;
      if(sessionId){
        try {
          const latestRes = await api.get(`/operations/sessions/${sessionId}/`);
          const latestNotes = latestRes.data?.notes || latestRes.data?.data?.notes || '';
          if(latestNotes && latestNotes.trim().startsWith('{')){
            const lp = JSON.parse(latestNotes);
            latestExistingEntries = lp.day_entries||[];
            latestLbh = lp.lbh||null;
          }
        } catch {}
      }
      const otherEntries = latestExistingEntries.filter(e=>e.entry_date!==(form.session_date||today));
      const allEntries = [...otherEntries,...todayEntriesWithDate];
      const workData = {
        user_notes: form.notes,
        day_entries: allEntries,
        hold_reason: form.hold_reason,
        lbh: latestLbh||{ length:form.lbh_length||'', breadth:form.lbh_breadth||'', height:form.lbh_height||'' },
      };
      const opMap = { DRILLING:'DRILLING', WIRE_SAW:'WIRE_SAW', BLASTING:'OTHER', OFFSET:'OTHER', WASTE:'OTHER', OTHER:'OTHER' };
      const firstWorkType = form.day_entries.find(e=>e.work_type)?.work_type||'OTHER';
      const payload = {
        operation_type: opMap[firstWorkType]||'OTHER',
        shift: form.shift, location: form.location||'',
        block_reference: refLabel, direction: form.direction||'',
        production_stage: form.production_stage||'', supervisor_name: form.supervisor_name||'',
        status: form.overall_status||'IN_PROGRESS',
        started_date: form.session_date,
        notes: JSON.stringify({
          ...workData,
          lbh:{
            length:  form.lbh_length  ||(form.production_stage==='BOX'?'10.5':form.production_stage==='PART'?'10.5':'3.5'),
            breadth: form.lbh_breadth ||(form.production_stage==='BOX'?'8.8' :form.production_stage==='PART'?'2.2' :'2.2'),
            height:  form.lbh_height  ||(form.production_stage==='GANGSAW'?'2.2':''),
          }
        }),
      };
      if(form.selected_block_id)   payload.block_id   = form.selected_block_id;
      if(form.selected_part_id)    payload.part_id    = form.selected_part_id;
      if(form.selected_gangsaw_id) payload.gangsaw_id = form.selected_gangsaw_id;

      let sessionId_saved;
      if(mode==='edit'&&sessionId){ await api.patch(`/operations/sessions/${sessionId}/`,payload); sessionId_saved=sessionId; }
      else { const res=await api.post('/operations/sessions/',payload); sessionId_saved=res.data?.data?.id; }

      if(form.overall_status==='COMPLETED'&&sessionId_saved){
        await api.post(`/operations/sessions/${sessionId_saved}/complete/`).catch(()=>{});
        const lbhL=parseFloat(form.lbh_length||(form.production_stage==='BOX'?'10.5':'10.5'))||10.5;
        const lbhB=parseFloat(form.lbh_breadth||(form.production_stage==='BOX'?'8.8':'2.2'))||8.8;
        const lbhH=parseFloat(form.lbh_height||'0')||0;
        if(form.production_stage==='BOX'&&form.selected_block_id)
          await api.patch(`/production/blocks/${form.selected_block_id}/`,{status:'COMPLETED',length:lbhL,width:lbhB,height:lbhH||undefined,direction:form.direction||undefined}).catch(()=>{});
        if(form.production_stage==='PART'&&form.selected_part_id)
          await api.patch(`/production/parts/${form.selected_part_id}/`,{status:'IN_GANGSAW',length:10.5,width:2.2,height:lbhH||undefined}).catch(()=>{});
        if(form.production_stage==='GANGSAW'&&form.selected_gangsaw_id)
          await api.patch(`/production/slabs/${form.selected_gangsaw_id}/`,{status:'PASSED'}).catch(()=>{});
      }
      navigate('/operations');
    } catch(err){
      const detail=err.response?.data?.errors?.detail||err.response?.data?.detail;
      setError(detail?String(detail):'Failed to save. Please check all fields.');
    } finally { setSaving(false); }
  };

  const title = isResume?`Resume Session — ${origSession?.session_number||''}`:mode==='edit'?`Edit Session — ${origSession?.session_number||''}`:'New Work Session';
  const subtitle = isResume?`Adding today's work · ${origSession?.block_reference||''}`:mode==='edit'?`${origSession?.block_reference||''} · ${origSession?.started_date||''}`:'Complete all sections';

  return (
    <Layout title={title} subtitle={subtitle} actions={<button className="btn btn-secondary btn-sm" onClick={()=>navigate('/operations')}>← Back</button>}>
      <div style={{ maxWidth:900 }}>
        <StepBar step={step}/>
        {error   && <div className="error-box" style={{ marginBottom:16 }}>{error}</div>}
        {warning && <div className="warning-box" style={{ display:'flex', gap:10, alignItems:'center', marginBottom:16 }}><AlertTriangle size={14} style={{ flexShrink:0 }}/>{warning}</div>}
        {isResume&&origSession&&(
          <div className="success-box" style={{ display:'flex', gap:10, alignItems:'center', marginBottom:16 }}>
            <CheckCircle size={16} style={{ flexShrink:0 }}/>Resuming session. Direction and Block auto-filled from previous entry.
          </div>
        )}

        {step===1&&(
          <div>
            <div className="card" style={{ marginBottom:16 }}>
              <ST>Section 1 — Session Header</ST>
              <div className="form-grid">
                <Field label="Date" required>
                  <input className="form-input" type="date" value={form.session_date} onChange={e=>setF('session_date',e.target.value)} max={new Date().toISOString().split('T')[0]}/>
                </Field>
                <Field label="Shift" required>
                  <select className="form-input" value={form.shift} onChange={e=>setF('shift',e.target.value)}>
                    {SHIFTS.map(s=><option key={s.val} value={s.val}>{s.label}</option>)}
                  </select>
                </Field>
                <Field label="Location">
                  <input className="form-input" value={form.location} onChange={e=>setF('location',e.target.value)} placeholder="e.g. North Face, Block Area 3"/>
                </Field>
                <Field label="Direction" required>
                  <select className="form-input" value={form.direction} onChange={async e=>{
                    const dir=e.target.value;
                    if(isResume&&origSession?.direction&&dir!==origSession.direction) setWarning(`Warning: Changing direction from "${origSession.direction}" to "${dir}".`);
                    else setWarning('');
                    setF('direction',dir);
                    setF('selected_block_id','');
                    setF('selected_part_id','');
                    setF('selected_gangsaw_id','');
                    if(dir){
                      // Prevent multiple simultaneous calls
                      if(isLoadingDir.current) return;
                      isLoadingDir.current = true;

                      const dirShort = DIRECTIONS.find(d=>d.val===dir)?.short||dir;

                      // Load all blocks for this direction
                      const blocksRes = await api.get(`/production/blocks/?direction=${dir}&page_size=200`);
                      const dirBlks = blocksRes.data.results||[];
                      const sortedBlks = [...dirBlks].sort((a,b)=>a.block_number.localeCompare(b.block_number));
                      const completedBlks = sortedBlks.filter(b=>b.status==='COMPLETED');
                      const incompleteBlks = sortedBlks.filter(b=>b.status!=='COMPLETED');

                      let detectedStage = 'BOX';
                      let foundBlockId = '';
                      let foundPartId = '';
                      let foundGangsawId = '';

                      // Sequential check: GANGSAW → PART → BOX
                      for(const block of completedBlks) {
                        try {
                          const pr = await api.get(`/production/parts/?block=${block.id}&page_size=100`);
                          const parts = (pr.data.results||[]).sort((a,b)=>a.part_number.localeCompare(b.part_number));

                          // Priority 1: GANGSAW — part IN_GANGSAW with incomplete slabs
                          const gangsawPart = parts.find(p=>p.status==='IN_GANGSAW');
                          if(gangsawPart) {
                            const slabsRes = await api.get(`/production/slabs/?part=${gangsawPart.id}&page_size=100`);
                            const slabs = slabsRes.data.results||[];
                            const incompleteSlab = slabs.find(g=>g.status==='IN_PROCESS'||g.status==='QC_PENDING');
                            if(incompleteSlab) {
                              detectedStage = 'GANGSAW';
                              foundBlockId = block.id;
                              foundPartId = gangsawPart.id;
                              foundGangsawId = incompleteSlab.id;
                              setParts(parts);
                              setGangsaws(slabs);
                              break;
                            }
                          }

                          // Priority 2: PART — block completed with available/in-process parts
                          const incompletePart = parts.find(p=>p.status==='AVAILABLE'||p.status==='IN_PROCESS');
                          if(incompletePart && detectedStage==='BOX') {
                            detectedStage = 'PART';
                            foundBlockId = block.id;
                            foundPartId = incompletePart.id;
                            setParts(parts);
                            break;
                          }
                        } catch {}
                      }

                      // Priority 3: BOX — find first incomplete block
                      if(detectedStage==='BOX') {
                        if(incompleteBlks.length>0) {
                          foundBlockId = incompleteBlks[0].id;
                          setDirBlocks(incompleteBlks);
                        } else if(dirBlks.length===0) {
                          // No blocks at all for this direction — ask to create
                          if(window.confirm(`No blocks found for ${dirShort} direction!\nAuto-create first ${dirShort}-BOX in Production?`)){
                            try{
                              const res=await api.post('/production/blocks/',{color:'GREEN',quality_grade:'A',length:'300.00',width:'150.00',height:'100.00',direction:dir});
                              const newBlock=res.data?.data||res.data;
                              const reloadRes=await api.get(`/production/blocks/?direction=${dir}&page_size=200`);
                              setDirBlocks(reloadRes.data.results||[]);
                              if(newBlock?.id) foundBlockId = newBlock.id;
                            } catch{ setError('Failed to create new block.'); }
                          }
                        } else {
                          // All blocks completed — ask to create next
                          if(window.confirm(`All ${dirShort} direction blocks completed!\nAuto-create next ${dirShort}-BOX in Production?`)){
                            try{
                              const res=await api.post('/production/blocks/',{color:'GREEN',quality_grade:'A',length:'300.00',width:'150.00',height:'100.00',direction:dir});
                              const newBlock=res.data?.data||res.data;
                              const reloadRes=await api.get(`/production/blocks/?direction=${dir}&page_size=200`);
                              const reloadSorted = (reloadRes.data.results||[]).sort((a,b)=>a.block_number.localeCompare(b.block_number));
                              setDirBlocks(reloadSorted.filter(b=>b.status!=='COMPLETED'));
                              if(newBlock?.id) foundBlockId = newBlock.id;
                            } catch{ setError('Failed to create new block.'); }
                          }
                        }
                      } else {
                        // PART or GANGSAW — show completed blocks in dropdown
                        setDirBlocks(completedBlks);
                      }

                      // Apply all detected values at once
                      setForm(f=>({
                        ...f,
                        production_stage: detectedStage,
                        selected_block_id: foundBlockId,
                        selected_part_id: foundPartId,
                        selected_gangsaw_id: foundGangsawId,
                      }));

                      isLoadingDir.current = false;
                    } else {
                      setDirBlocks([]);
                      isLoadingDir.current = false;
                    }
                  }}>
                    <option value="">Select direction</option>
                    {DIRECTIONS.map(d=><option key={d.val} value={d.val}>{d.label}</option>)}
                  </select>
                </Field>
                <Field label="Supervisor Name" required>
                  <select className="form-input" value={form.supervisor_name} onChange={e=>setF('supervisor_name',e.target.value)}>
                    <option value="">Select supervisor</option>
                    {employees.map(e=><option key={e.id} value={e.full_name}>{e.full_name}{e.designation?' — '+e.designation:''}</option>)}
                  </select>
                </Field>
              </div>
            </div>

            <div className="card" style={{ marginBottom:16 }}>
              <ST>Section 2 — Production Stage *</ST>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:10 }}>
                {PRODUCTION_STAGES.map(ps=>(
                  <div key={ps.val} onClick={async()=>{
                    setF('production_stage',ps.val);
                    setF('selected_block_id','');
                    setF('selected_part_id','');
                    setF('selected_gangsaw_id','');
                    // Re-filter blocks based on new stage
                    if(form.direction && ps.val!=='GENERAL') {
                      await filterBlocksByDirection(form.direction, allBlocks, ps.val);
                    }
                  }} style={{ padding:'14px 16px', borderRadius:'var(--radius-sm)', cursor:'pointer', border:`2px solid ${form.production_stage===ps.val?'var(--brand)':'var(--border)'}`, background:form.production_stage===ps.val?'var(--brand-dim)':'var(--bg-input)' }}>
                    <div style={{ fontSize:13, fontWeight:700, color:form.production_stage===ps.val?'var(--brand)':'var(--text-primary)' }}>{ps.label}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:3 }}>{ps.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {form.production_stage&&form.production_stage!=='GENERAL'&&(
              <div className="card" style={{ marginBottom:16 }}>
                <ST>Section 3 — Reference Linking</ST>
                <div className="form-grid">
                  <Field label="Block (BOX) *">
                    {form.direction&&form.selected_block_id&&(()=>{
                      const selBlock=allBlocks.find(b=>b.id===form.selected_block_id);
                      if(!selBlock) return null;
                      const serial=(selBlock.block_number||'').split('/').pop()||'?';
                      return (
                        <div style={{ marginBottom:8, padding:'7px 12px', background:'var(--brand-dim)', borderRadius:'var(--radius-sm)', fontSize:12, color:'var(--brand)', fontWeight:600 }}>
                          ✓ Auto-selected: {getDirShort(form.direction)}-BOX/{serial} ({selBlock.color})
                        </div>
                      );
                    })()}
                    <select className="form-input" value={form.selected_block_id} onChange={e=>{setF('selected_block_id',e.target.value);setF('selected_part_id','');setF('selected_gangsaw_id','');}}>
                      <option value="">Select Block</option>
                      {(form.production_stage==='PART'||form.production_stage==='GANGSAW'
                        ? dirBlocks.filter(b=>b.status==='COMPLETED')
                        : dirBlocks
                      ).map(b=>{
                        const serial=(b.block_number||'').split('/').pop()||'?';
                        const statusLabel=b.status==='COMPLETED'?'✓ Done':b.status==='IN_PROCESS'?'[In Progress]':'[New]';
                        const label=form.direction
                          ?`${getDirShort(form.direction)}-BOX/${serial} (${b.color}) ${statusLabel}`
                          :`${b.block_number} (${b.color}) ${statusLabel}`;
                        return <option key={b.id} value={b.id}>{label}</option>;
                      })}
                    </select>
                    {form.direction&&dirBlocks.length===0&&(
                      <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:6 }}>
                        No blocks found for {DIRECTIONS.find(d=>d.val===form.direction)?.label} direction. Select direction to auto-load.
                      </div>
                    )}
                  </Field>

                  {(form.production_stage==='PART'||form.production_stage==='GANGSAW')&&(
                    <Field label="Part Reference *">
                      <select className="form-input" value={form.selected_part_id} onChange={e=>{setF('selected_part_id',e.target.value);setF('selected_gangsaw_id','');}}>
                        <option value="">Select Part</option>
                        {parts.map(p=><option key={p.id} value={p.id}>{selectedBlock?getPartLabel(form.direction,selectedBlock,p):p.part_code} ({p.status})</option>)}
                      </select>
                    </Field>
                  )}

                  {form.production_stage==='GANGSAW'&&(
                    <Field label="Gangsaw Reference *">
                      <select className="form-input" value={form.selected_gangsaw_id} onChange={e=>setF('selected_gangsaw_id',e.target.value)}>
                        <option value="">Select Gangsaw</option>
                        {gangsaws.map(g=><option key={g.id} value={g.id}>{selectedBlock&&selectedPart?getGangsawLabel(form.direction,selectedBlock,selectedPart,g):g.slab_code} ({g.status})</option>)}
                      </select>
                    </Field>
                  )}
                </div>
                {refLabel&&(
                  <div style={{ marginTop:12, padding:'10px 14px', background:'var(--brand-dim)', borderRadius:'var(--radius-sm)', fontSize:12, color:'var(--brand)', fontWeight:700 }}>
                    Reference: {refLabel}
                  </div>
                )}
              </div>
            )}

            {(form.production_stage==='BOX'||form.production_stage==='PART'||form.production_stage==='GANGSAW')&&(
              <div className="card" style={{ marginBottom:16 }}>
                <ST>
                  {form.production_stage==='BOX'&&'Block Dimensions (LBH)'}
                  {form.production_stage==='PART'&&'Part Dimensions (LBH)'}
                  {form.production_stage==='GANGSAW'&&'Gangsaw Slab Dimensions (LBH)'}
                </ST>
                <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:14 }}>
                  {form.production_stage==='BOX'&&'Default values pre-filled. Enter the actual Height of this block.'}
                  {form.production_stage==='PART'&&'Default values pre-filled. Enter the actual Height after cutting.'}
                  {form.production_stage==='GANGSAW'&&'All values are standard — confirm below.'}
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Length (m)</label>
                    <input className="form-input" type="number" step="0.01" value={form.lbh_length||(form.production_stage==='BOX'?'10.5':form.production_stage==='PART'?'10.5':'3.5')} onChange={e=>setF('lbh_length',e.target.value)}/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Breadth (m)</label>
                    <input className="form-input" type="number" step="0.01" value={form.lbh_breadth||(form.production_stage==='BOX'?'8.8':form.production_stage==='PART'?'2.2':'2.2')} onChange={e=>setF('lbh_breadth',e.target.value)}/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Height (m) {(form.production_stage==='BOX'||form.production_stage==='PART')&&<span style={{ color:'var(--red)',marginLeft:3 }}>*</span>}</label>
                    <input className="form-input" type="number" step="0.01" value={form.lbh_height||(form.production_stage==='GANGSAW'?'2.2':'')} onChange={e=>setF('lbh_height',e.target.value)} placeholder={form.production_stage==='GANGSAW'?'2.2':'Enter height in meters'}/>
                  </div>

                </div>
              </div>
            )}

            <div className="card" style={{ marginBottom:16 }}>
              <ST>Overall Working Status {form.production_stage?`— ${form.production_stage}`:''}</ST>
              {form.production_stage==='BOX'&&<div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:12 }}>Select "Completed" only when ALL work on this BOX is done → auto-creates P1–P4.</div>}
              {form.production_stage==='PART'&&<div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:12 }}>Select "Completed" only when ALL work on this PART is done → auto-creates G1–G9.</div>}
              {form.production_stage==='GANGSAW'&&<div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:12 }}>Select "Completed" when gangsaw work is done → slab moves to Final Stock.</div>}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(100px, 1fr))', gap:12 }}>
                {OVERALL_STATUS_OPTIONS.map(opt=>(
                  <div key={opt.val} onClick={()=>setF('overall_status',opt.val)} style={{ padding:'14px', borderRadius:'var(--radius-sm)', cursor:'pointer', textAlign:'center', border:`2px solid ${form.overall_status===opt.val?'var(--brand)':'var(--border)'}`, background:form.overall_status===opt.val?'var(--brand-dim)':'var(--bg-input)' }}>
                    <div style={{ fontSize:14, fontWeight:700, color:form.overall_status===opt.val?'var(--brand)':'var(--text-primary)' }}>{opt.label}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>{opt.desc}</div>
                  </div>
                ))}
              </div>
              {form.overall_status==='ON_HOLD'&&(
                <div style={{ marginTop:14 }}>
                  <Field label="Hold Reason *">
                    <select className="form-input" value={form.hold_reason} onChange={e=>setF('hold_reason',e.target.value)}>
                      <option value="">Select reason</option>{HOLD_REASONS.map(r=><option key={r}>{r}</option>)}
                    </select>
                  </Field>
                </div>
              )}
            </div>

            <div className="card" style={{ marginBottom:16 }}>
              <ST>Notes (Optional)</ST>
              <textarea className="form-input" rows={2} value={form.notes} onChange={e=>setF('notes',e.target.value)} placeholder="Any additional observations..."/>
            </div>

            <div style={{ display:'flex', justifyContent:'flex-end' }}>
              <button className="btn btn-primary" onClick={()=>{ const err=validateStep1(); if(err){setError(err);return;} setError('');setStep(2); }}>
                Next — Work Details <ChevronRight size={15}/>
              </button>
            </div>
          </div>
        )}

        {step===2&&(
          <div>
            <div className="card" style={{ marginBottom:16 }}>
              <ST>Work Entries for {form.session_date} — {SHIFTS.find(s=>s.val===form.shift)?.label}</ST>
              <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:16 }}>Add all work done. Each operation has its own status.</div>
              <DayWorkEntries entries={form.day_entries} onChange={e=>setF('day_entries',e)} employees={employees}/>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between' }}>
              <button className="btn btn-secondary" onClick={()=>setStep(1)}><ChevronLeft size={15}/> Back</button>
              <button className="btn btn-primary" onClick={()=>{ 
                if(form.day_entries.every(e=>!e.work_type)){setError('Please add at least one work entry.');return;}
                const err2=validateStep2(); if(err2){setError(err2);return;}
                setError('');setStep(3); }}>
                Next — Review <ChevronRight size={15}/>
              </button>
            </div>
          </div>
        )}

        {step===3&&(
          <div>
            <div className="card" style={{ marginBottom:16 }}>
              <ST>Session Summary</ST>
              {[
                ['Date',form.session_date],
                ['Shift',SHIFTS.find(s=>s.val===form.shift)?.label],
                ['Direction',DIRECTIONS.find(d=>d.val===form.direction)?.label||'—'],
                ['Location',form.location||'—'],
                ['Supervisor',form.supervisor_name||'—'],
                ['Production Stage',form.production_stage||'—'],
                ['Reference',refLabel||'—'],
                ['Overall Status',form.overall_status],
                ['Hold Reason',form.hold_reason||'—'],
                ['Work Types',form.day_entries.filter(e=>e.work_type).map(e=>`${WORK_TYPES.find(w=>w.val===e.work_type)?.label||e.work_type} (${e.status||'IN_PROGRESS'})`).join(', ')||'—'],
                ['Notes',form.notes||'—'],
              ].map(([label,val],i)=>(
                <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid var(--border-light)' }}>
                  <span style={{ fontSize:11, color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', minWidth:160 }}>{label}</span>
                  <span style={{ fontSize:13, color:'var(--text-primary)', textAlign:'right', flex:1 }}>{val}</span>
                </div>
              ))}
            </div>
            <div className="success-box" style={{ display:'flex', gap:10, alignItems:'center', marginBottom:16 }}>
              <CheckCircle size={16} style={{ flexShrink:0 }}/>All sections complete. Save below.
            </div>
            {form.overall_status==='COMPLETED'&&(
              <div className="warning-box" style={{ display:'flex', gap:10, alignItems:'center', marginBottom:16 }}>
                <AlertTriangle size={15} style={{ flexShrink:0 }}/>
                {form.production_stage==='BOX'&&'Marking BOX as Completed will auto-create Parts P1–P4 in Production.'}
                {form.production_stage==='PART'&&'Marking PART as Completed will auto-create Gangsaw Slabs G1–G9 in Production.'}
                {form.production_stage==='GANGSAW'&&'Marking GANGSAW as Completed will move slab to Final Stock.'}
              </div>
            )}
            <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
              <button className="btn btn-secondary" onClick={()=>setStep(2)}><ChevronLeft size={15}/> Back</button>
              <div style={{ display:'flex', gap:10 }}>
                <button className="btn btn-secondary" onClick={()=>navigate('/operations')}>Cancel</button>
                <button className="btn btn-primary" disabled={saving} onClick={handleSave}>
                  {saving?'Saving...':(mode==='edit'?'Update Session':'Save Session')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}